// src/components/Notifications/PushOptInModal.tsx
// Modale post-login : active le push (switch) — pas de croix, masque non cliquable.

import React, { useEffect, useState } from 'react';
import { Modal, Switch, Typography, Alert, Button } from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  MobileOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { PUSH_ELIGIBLE_TYPES } from '../../utils/pushEligibleTypes';
import type { UserPushPrefs } from '../../types/pushPrefs';
import { DEFAULT_PUSH_QUIET_TIMEZONE } from '../../types/pushPrefs';
import { saveUserPushPrefs } from '../../services/pushPrefsService';
import { isAppLocale } from '../../i18n';

const { Title, Paragraph, Text } = Typography;

const BRAND = '#E91E63';
const BRAND_SOFT = '#d946ef';

const SNOOZE_STORAGE_PREFIX = 'nauvatek_push_optin_snooze_until_';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // 3 jours
const SHOW_DELAY_MS = 1800;
/** Aligné sur GenericLayout mobile (≤768). */
const MOBILE_MAX_WIDTH_PX = 768;

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH_PX}px)`).matches;
}

function snoozeKey(uid: string): string {
  return `${SNOOZE_STORAGE_PREFIX}${uid}`;
}

function isSnoozed(uid: string): boolean {
  try {
    const raw = window.localStorage.getItem(snoozeKey(uid));
    if (!raw) return false;
    const until = Number(raw);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

function setSnooze(uid: string): void {
  try {
    window.localStorage.setItem(snoozeKey(uid), String(Date.now() + SNOOZE_MS));
  } catch {
    // ignore
  }
}

const PushOptInModal: React.FC = () => {
  const { t } = useAppTranslation('notifications');
  const { user, userProfile, patchUserProfile } = useAuth();
  const {
    permission,
    isSupported,
    isRegistered,
    isRegistering,
    vapidConfigured,
    enablePush,
  } = usePushNotifications();

  const [open, setOpen] = useState(false);
  const [switchOn, setSwitchOn] = useState(false);

  const uid = user?.uid;
  const hasPreferredLocale = isAppLocale(userProfile?.preferredLocale);

  useEffect(() => {
    if (!uid) {
      setOpen(false);
      return;
    }
    // Langue d’abord (modale PreferredLocale) — évite deux modales empilées
    if (!hasPreferredLocale) {
      setOpen(false);
      return;
    }
    // Modale « forcée » = téléphone uniquement (desktop = cloche / Paramètres)
    if (!isMobileViewport()) {
      setOpen(false);
      return;
    }
    if (!isSupported || !vapidConfigured) return;
    if (isRegistered || permission === 'granted') {
      setOpen(false);
      return;
    }
    // Permission déjà bloquée côté navigateur : pas de modale (évite le message contradictoire)
    if (permission === 'denied') {
      setOpen(false);
      return;
    }
    if (isSnoozed(uid)) return;

    const timer = window.setTimeout(() => {
      if (!isMobileViewport() || isSnoozed(uid)) return;
      setOpen(true);
    }, SHOW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [uid, hasPreferredLocale, isSupported, vapidConfigured, isRegistered, permission]);

  const enableAllPrefs = async () => {
    if (!uid) return;
    const next: UserPushPrefs = {
      enabled: true,
      types: Object.fromEntries(
        PUSH_ELIGIBLE_TYPES.map((type) => [type, true])
      ) as UserPushPrefs['types'],
      quietHours: {
        enabled: false,
        startHour: 21,
        endHour: 7,
        timeZone: DEFAULT_PUSH_QUIET_TIMEZONE,
      },
    };
    try {
      const saved = await saveUserPushPrefs(uid, next);
      patchUserProfile({ pushPrefs: saved });
    } catch {
      // enablePush a déjà pu réussir — prefs best-effort
    }
  };

  const handleSwitch = async (checked: boolean) => {
    if (!checked) {
      setSwitchOn(false);
      return;
    }
    setSwitchOn(true);
    const result = await enablePush();
    if (result.ok) {
      await enableAllPrefs();
      setOpen(false);
      return;
    }
    setSwitchOn(false);
  };

  const handleLater = () => {
    if (uid) setSnooze(uid);
    setOpen(false);
  };

  if (!uid || !isSupported || !vapidConfigured) return null;
  if (!isMobileViewport()) return null;

  const benefits = [
    { icon: <CalendarOutlined />, text: t('push.modalBenefitRdv') },
    { icon: <WarningOutlined />, text: t('push.modalBenefitStock') },
    { icon: <BellOutlined />, text: t('push.modalBenefitReports') },
  ];

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      destroyOnClose
      width={440}
      styles={{
        content: {
          padding: 0,
          overflow: 'hidden',
          borderRadius: 16,
        },
        mask: {
          backdropFilter: 'blur(4px)',
          background: 'rgba(15, 10, 20, 0.55)',
        },
      }}
    >
      <div
        style={{
          background: `linear-gradient(145deg, ${BRAND} 0%, ${BRAND_SOFT} 55%, #a21caf 100%)`,
          padding: '28px 24px 22px',
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 14px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid rgba(255,255,255,0.45)',
          }}
        >
          <MobileOutlined style={{ fontSize: 30, color: '#fff' }} />
        </div>
        <Title level={4} style={{ color: '#fff', margin: 0, fontWeight: 700 }}>
          {t('push.modalTitle')}
        </Title>
        <Paragraph
          style={{
            color: 'rgba(255,255,255,0.92)',
            margin: '10px 0 0',
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {t('push.modalLead')}
        </Paragraph>
      </div>

      <div style={{ padding: '20px 22px 16px' }}>
        <Paragraph
          style={{
            marginBottom: 14,
            fontSize: 14,
            lineHeight: 1.55,
            color: '#434343',
          }}
        >
          {t('push.modalDesc')}
        </Paragraph>

        <div
          style={{
            background: '#fff7fb',
            border: '1px solid rgba(233, 30, 99, 0.18)',
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 16,
          }}
        >
          {benefits.map((item) => (
            <div
              key={item.text}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                marginBottom: 8,
              }}
            >
              <CheckCircleOutlined style={{ color: BRAND, marginTop: 3 }} />
              <Text style={{ fontSize: 13, color: '#262626', flex: 1 }}>
                <span style={{ marginRight: 6, color: BRAND_SOFT }}>{item.icon}</span>
                {item.text}
              </Text>
            </div>
          ))}
        </div>

        {permission === 'denied' ? (
          <Alert type="warning" showIcon message={t('push.denied')} />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              padding: '14px 16px',
              background: `linear-gradient(90deg, rgba(233,30,99,0.08), rgba(217,70,239,0.08))`,
              borderRadius: 12,
              border: `1px solid ${BRAND}`,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <Text strong style={{ display: 'block', color: '#111', fontSize: 15 }}>
                {t('push.modalSwitchLabel')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('push.modalSwitchHint')}
              </Text>
            </div>
            <Switch
              checked={switchOn}
              loading={isRegistering}
              style={{ background: switchOn ? BRAND : undefined }}
              onChange={(checked) => {
                void handleSwitch(checked);
              }}
            />
          </div>
        )}

        {permission !== 'denied' && (
          <Text
            type="secondary"
            style={{
              fontSize: 12,
              display: 'block',
              marginTop: 14,
              lineHeight: 1.45,
              textAlign: 'center',
            }}
          >
            {t('push.modalHint')}
          </Text>
        )}

        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Button
            type="link"
            size="small"
            onClick={handleLater}
            style={{ color: '#8c8c8c', fontSize: 12 }}
          >
            {t('push.modalLater')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PushOptInModal;
